<script setup lang="ts">
import { ref } from 'vue';
import Select from 'primevue/select';
import MultiSelect from 'primevue/multiselect';
import DatePicker from 'primevue/datepicker';
import InputText from 'primevue/inputtext';
import { TimezoneSelector, type Timezone } from '@/components';
import { formattedDate } from '@/utils';

const props = defineProps<{
  type: string;
  cronValue: string;
  random?: string | null;
  timezone: Timezone;
}>();

const [minutes, hours, dayOfMonth, month, dayOfWeek] =
  props.cronValue.split(' ');

const selectDayWeekOptions = ref(['day', 'week']);
const selectSetTimeRandomOptions = ref(['set time', 'random']);
const daysOfWeek = ref([
  {
    label: 'Monday',
    value: 1
  },
  {
    label: 'Tuesday',
    value: 2
  },
  {
    label: 'Wednesday',
    value: 3
  },
  {
    label: 'Thursday',
    value: 4
  },
  {
    label: 'Friday',
    value: 5
  },
  {
    label: 'Saturday',
    value: 6
  },
  {
    label: 'Sunday',
    value: 7
  }
]);

const selctedDaysOfWeek = ref(
  dayOfWeek !== '*' ?
    daysOfWeek.value.filter(day =>
      dayOfWeek.split(',').includes(`${day.value}`)
    ) :
    []
);
const timeUnit = ref(
  dayOfWeek === '*' ?
    selectDayWeekOptions.value[0] :
    selectDayWeekOptions.value[1]
);
const randomness = ref(
  props.random ?
    selectSetTimeRandomOptions.value[1] :
    selectSetTimeRandomOptions.value[0]
);
const randomFromTime = ref(!props.random ? undefined : new Date(2020, 0, 1, ...props.random.split(';')[0].split(':').map(Number)));
const randomToTime = ref(!props.random ? undefined : new Date(2020, 0, 1, ...props.random.split(';')[1].split(':').map(Number)));
const setTime = ref(new Date(2020, 0, 1, parseInt(hours), parseInt(minutes)));
const selectedTimezone = ref(props.timezone);
const onDays = ref(dayOfWeek !== '*' ? dayOfWeek : '');

const emits = defineEmits(['onChange', 'update:random', 'update:cronValue', 'update:timezone']);
const onChange = (entity: string) => {
  const formatteTime = formattedDate(setTime.value, 'HH:mm').split(':');
  let nextMinutes = `${formatteTime[1]}`;
  let nextHours = `${formatteTime[0]}`;
  const nextDayOfMonth = dayOfMonth;
  const nextMonth = month;
  let nextDayOfWeek = timeUnit.value === selectDayWeekOptions.value[0] ? '*' : selctedDaysOfWeek.value.map(day => `${day.value}`).join(',');
  if (entity === 'every-select') {
    if (timeUnit.value === selectDayWeekOptions.value[0]) {
      nextDayOfWeek = '*';
    } else {
      nextDayOfWeek = selctedDaysOfWeek.value.map(day => `${day.value}`).join(',');
    }
  } else if (entity === 'set-time') {
    const value = formattedDate(setTime.value, 'HH:mm').split(':');
    nextHours = value[0];
    nextMinutes = value[1];
  } else if (entity === 'on-days-input') {
    nextDayOfWeek = onDays.value;
  }
  const nextCronValue = `${nextMinutes} ${nextHours} ${nextDayOfMonth} ${nextMonth} ${nextDayOfWeek}`;
  emits('update:cronValue', nextCronValue);
};

const onRandomTimeChanges = (field: string) => {
  let nextFrom, nextTo, nextRandomness;
  if (randomness.value) {
    [nextFrom, nextTo] = props.random ? props.random!.split(';') : ['00:00', '23:59'];
  }
  if (field === 'from-time') {
    nextFrom = formattedDate(randomFromTime.value!, 'HH:mm');
    nextRandomness = `${nextFrom};${nextTo}`;
  } else if (field === 'to-time') {
    nextTo = formattedDate(randomToTime.value!, 'HH:mm');
    nextRandomness = `${nextFrom};${nextTo}`;
  } else if (field === 'select' && randomness.value == selectSetTimeRandomOptions.value[1]) {
    nextRandomness = '00:00;23:59';
  }
  emits('update:random', nextRandomness);
};

const onTimezoneChange = () => {
  emits('update:timezone', selectedTimezone.value);
};
</script>

<template>
  <div class="selector">
    <div class="labels every" v-if="type === 'rolling'">Every</div>
    <Select v-model="timeUnit"
      class="every-select"
      v-if="type === 'rolling'"
      @change="onChange('every-select')"
      :options="selectDayWeekOptions"/>

    <div class="labels on-days" v-if="type === 'fixed'">On day(s)</div>
    <InputText type="text"
      v-model="onDays"
      @input="onChange('on-days-input')"
      v-if="type === 'fixed'"
      class="on-days-input"/>

    <div class="select-week-days" v-if="timeUnit === selectDayWeekOptions[1] && type === 'rolling'">
      <div class="labels on">on</div>
      <MultiSelect v-model="selctedDaysOfWeek"
        :options="daysOfWeek"
        @change="onChange('every-select')"
        optionLabel="label"
        placeholder=""
        class="days-of-weeks"/>
    </div>

    <div class="labels at">at</div>

    <Select v-model="randomness"
      class="at-select"
      @change="onRandomTimeChanges('select')"
      :options="selectSetTimeRandomOptions"/>

    <div class="set-time" v-if="randomness === selectSetTimeRandomOptions[0]">
      <DatePicker v-model="setTime"
        @input="onChange('set-time')"
        @date-select="onChange('set-time')"
        class="time-picker"
        timeOnly />
    </div>

    <div class="set-time-between"
      v-if="randomness === selectSetTimeRandomOptions[1]">
      <div class="labels at">between</div>
      <DatePicker v-model="randomFromTime"
        @input="onRandomTimeChanges('from-time')"
        @date-select="onRandomTimeChanges('from-time')"
        class="time-picker"
        timeOnly
        fluid />
      <div class="labels and">and</div>
      <DatePicker v-model="randomToTime"
        @input="onRandomTimeChanges('to-time')"
        @date-select="onRandomTimeChanges('to-time')"
        class="time-picker"
        timeOnly
        fluid />
    </div>

    <div class="timezone-select">
      <TimezoneSelector v-model:timezone="selectedTimezone" @onChange="onTimezoneChange"/>
    </div>
  </div>
</template>

<style scoped lang="scss">
.selector {
  display: flex;
  align-items: center;

  .labels {
    padding: 0 4px 0 4px;
  }

  .on-days-input {
    max-width: 120px;
  }

  .select-week-days {
    display: flex;
    align-items: center;

    .days-of-weeks {
      max-width: 150px;
    }
  }

  .set-time-between {
    display: flex;
    align-items: center;
  }

  .time-picker {
    min-width: 80px;
    margin: 0 4px 0 4px;
  }

  .timezone-select {
    margin-left: 8px;
  }
}
</style>
